import React, { useState, useEffect } from 'react';
import { Box, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../../amplify/data/resource';
import ReactLoading from 'react-loading';
import awsConfig from '../../../../amplify_outputs.json';
import { useSnackbar } from 'notistack';
import NewBadge from '../../../../components/icons/NewBadge';
import { generateClient } from 'aws-amplify/api';
import SalaryMonthsDisplay from '../../../../components/custom/SalaryMonthsDisplay';
import EmployeesList from '../EmployeesList/EmployeesList';
import { useAuth } from '../../../../utils/context/useAuthContext';
import { formatMoney } from '../../../../utils/format/format';

const fileSelectionSet = [
  'id',
  'createdAt',
  'status',
  'salaryMonth',
  'paymentDate',
  'lastSentToMislaka',
  'user.companyName',
  'user.companyRegistrationNumber',
] as const;

const depositSelectionSet = [
  'id',
  'totalDepositAmount',
] as const;

const employeeSelectionSet = [
  'id',
  'firstName',
  'lastName',
  'employeeId'
] as const;

const fundContributionSelectionSet = [
  'id',
  'contributionType',
  'contributionAmount',
  'contributionRate',
  'exemptPayments',
  'registryIdentifier',
  'previousRegistryIdentifier',
  'status',
  'error',
] as const;

// @ts-ignore
type File = SelectionSet<Schema['File']['type'], typeof fileSelectionSet>;

// @ts-ignore
type Deposit = SelectionSet<Schema['Deposit']['type'], typeof depositSelectionSet>;

// @ts-ignore
type Employee = SelectionSet<Schema['Employee']['type'], typeof employeeSelectionSet>;

// @ts-ignore
type FundContribution = SelectionSet<Schema['FundContribution']['type'], typeof fundContributionSelectionSet>;

type FileWithRelatedData = File & {
  employees: Employee[];
  deposits: Deposit[];
  fundContributions: FundContribution[];
};

interface FileItemProps {
  file: FileWithRelatedData;
}

const getFileIcon = (status: String) => {
  switch (status) {
    case 'COMPLETED':
      return '/assets/dashboard/fileGreen.svg';
    case 'IN_PROGRESS':
      return '/assets/dashboard/fileYellow.svg';
    case 'PAID':
      return '/assets/dashboard/fileYellow.svg';
    case 'PENDING':
      return '/assets/dashboard/fileGrey.svg';
    case 'REJECTED':
      return '/assets/dashboard/fileRed.svg';
    default:
      return '/assets/dashboard/fileGrey.svg';
  }
};

const getFileIconTextColor = (status: String) => {
  switch (status) {
    case 'COMPLETED':
      return '#94C729';
    case 'IN_PROGRESS':
      return '#F6B209';
    case 'PAID':
      return '#F6B209';
    case 'PENDING':
      return '#808080';
    case 'REJECTED':
      return '#FF6F78';
    default:
      return '#808080';
  }
};

const apiClient = generateClient<Schema>();

const FileItem: React.FC<FileItemProps> = ({ file }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [openDetails, setOpenDetails] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const handleToggleDetails = () => {
    setOpenDetails((prev) => !prev);
  };

  const handleRowClick = () => {
    setOpenDetails((prev) => !prev);
  };

  // Function for detailed calculation logging (called only on button click)
  const logTotalCalculations = () => {
    console.log(`[TOTAL CALCULATION] File ID: ${file.id}`);
    console.log(`[TOTAL CALCULATION] Deposits:`, file?.deposits);
    
    // Deposits logging
    const totalToBePaidDetailed = Array.isArray(file?.deposits)
      ? file.deposits.reduce(
        (accumulator: number, deposit: { totalDepositAmount?: number }, index: number) => {
          const depositAmount = deposit.totalDepositAmount || 0;
          console.log(`[TOTAL CALCULATION] Deposit ${index}: ${depositAmount}`);
          return accumulator + depositAmount;
        },
        0,
      )
      : 0;

    console.log(`[TOTAL CALCULATION] Total amount to be paid (totalToBePaid): ${totalToBePaidDetailed}`);
    console.log(`[TOTAL CALCULATION] Fund contributions:`, file?.fundContributions);

    // Approved contributions logging
    const totalAcceptedDetailed = Array.isArray(file?.fundContributions)
      ? file.fundContributions.reduce(
        (accumulator: number, contribution: FundContribution, index: number) => {
          const isAccepted = contribution.status === 'ACCEPTED' || contribution.status === 'ACCEPTED_WITH_ERROR_AKA';
          const contributionAmount = contribution.contributionAmount || 0;
          
          console.log(`[TOTAL CALCULATION] Contribution ${index}: status=${contribution.status}, amount=${contributionAmount}, accepted=${isAccepted}`);
          
          if (isAccepted) {
            return accumulator + contributionAmount;
          }
          return accumulator;
        },
        0,
      )
      : 0;

    console.log(`[TOTAL CALCULATION] Total accepted contributions (totalAcceptedContributions): ${totalAcceptedDetailed}`);

    // Processing contributions logging
    const totalProcessingDetailed = Array.isArray(file?.fundContributions)
      ? file.fundContributions.reduce(
        (accumulator: number, contribution: FundContribution, index: number) => {
          const isProcessing = contribution.status === 'PROCESSING';
          const contributionAmount = contribution.contributionAmount || 0;
          
          console.log(`[TOTAL CALCULATION] Contribution ${index} (processing): status=${contribution.status}, amount=${contributionAmount}, processing=${isProcessing}`);
          
          if (isProcessing) {
            return accumulator + contributionAmount;
          }
          return accumulator;
        },
        0,
      )
      : 0;

    console.log(`[TOTAL CALCULATION] Total processing contributions (totalProcessingContributions): ${totalProcessingDetailed}`);

    // Action required contributions logging
    const totalActionRequiredDetailed = Array.isArray(file?.fundContributions)
      ? file.fundContributions.reduce(
        (accumulator: number, contribution: FundContribution, index: number) => {
          const isActionRequired = contribution.status === 'ACTION_REQUIRED';
          const contributionAmount = contribution.contributionAmount || 0;
          
          console.log(`[TOTAL CALCULATION] Contribution ${index} (action required): status=${contribution.status}, amount=${contributionAmount}, action_required=${isActionRequired}`);
          
          if (isActionRequired) {
            return accumulator + contributionAmount;
          }
          return accumulator;
        },
        0,
      )
      : 0;

    console.log(`[TOTAL CALCULATION] Total action required contributions (totalActionRequiredContributions): ${totalActionRequiredDetailed}`);

    // Logic error checking
    const totalAllContributions = totalAcceptedDetailed + totalProcessingDetailed + totalActionRequiredDetailed;
    console.log(`[TOTAL CALCULATION] Sum of all contributions (accepted + processing + action required): ${totalAllContributions}`);

    // CRITICAL CHECKS
    if (totalAcceptedDetailed > totalToBePaidDetailed) {
      console.error(`🚨 [LOGIC ERROR] Accepted contributions amount (${totalAcceptedDetailed}) is greater than total file amount (${totalToBePaidDetailed})!`);
      console.error(`🚨 [LOGIC ERROR] File ID: ${file.id}`);
      console.error(`🚨 [LOGIC ERROR] Difference: ${totalAcceptedDetailed - totalToBePaidDetailed}`);
    }

    if (totalAllContributions > totalToBePaidDetailed) {
      console.error(`🚨 [LOGIC ERROR] Total sum of all contributions (${totalAllContributions}) is greater than total file amount (${totalToBePaidDetailed})!`);
      console.error(`🚨 [LOGIC ERROR] File ID: ${file.id}`);
      console.error(`🚨 [LOGIC ERROR] Difference: ${totalAllContributions - totalToBePaidDetailed}`);
    }

    const acceptedPercentageDetailed = totalToBePaidDetailed > 0
      ? Math.round((totalAcceptedDetailed / totalToBePaidDetailed) * 100)
      : 0;

    console.log(`[TOTAL CALCULATION] Percentage of accepted contributions: ${acceptedPercentageDetailed}%`);
    console.log(`[TOTAL CALCULATION] ==========================================`);
  };

  // Function for downloading logs as text file
  const downloadLogsAsFile = () => {
    let logContent = '';
    
    // File header
    logContent += `FILE CALCULATIONS REPORT\n`;
    logContent += `=====================================================\n`;
    logContent += `File ID: ${file.id}\n`;
    logContent += `Creation Date: ${file.createdAt || 'Not specified'}\n`;
    logContent += `Status: ${file.status || 'Not specified'}\n`;
    logContent += `Salary Month: ${file.salaryMonth || 'Not specified'}\n`;
    logContent += `Company: ${file.user.companyName || 'Not specified'}\n`;
    logContent += `Registration Number: ${file.user.companyRegistrationNumber || 'Not specified'}\n`;
    logContent += `=====================================================\n\n`;

    // Deposits analysis
    logContent += `DEPOSITS ANALYSIS:\n`;
    logContent += `------------------\n`;
    
         let totalToBePaidDetailed = 0;
     if (Array.isArray(file?.deposits)) {
       (file.deposits as { totalDepositAmount?: number }[]).forEach((deposit, index) => {
         const depositAmount = deposit.totalDepositAmount || 0;
         logContent += `Deposit ${index + 1}: ${depositAmount.toLocaleString()} ₪\n`;
         totalToBePaidDetailed += depositAmount;
       });
     } else {
       logContent += `No deposits found\n`;
     }
    
    logContent += `------------------\n`;
    logContent += `TOTAL AMOUNT TO BE PAID: ${totalToBePaidDetailed.toLocaleString()} ₪\n\n`;

    // Fund contributions analysis
    logContent += `FUND CONTRIBUTIONS ANALYSIS:\n`;
    logContent += `----------------------------\n`;
    
    let totalAcceptedDetailed = 0;
    let totalProcessingDetailed = 0;
    let totalActionRequiredDetailed = 0;

         if (Array.isArray(file?.fundContributions)) {
       (file.fundContributions as FundContribution[]).forEach((contribution, index) => {
         const contributionAmount = contribution.contributionAmount || 0;
         const status = contribution.status || 'NOT SPECIFIED';
        
        logContent += `Contribution ${index + 1}:\n`;
        logContent += `  - Status: ${status}\n`;
        logContent += `  - Amount: ${contributionAmount.toLocaleString()} ₪\n`;
        logContent += `  - Contribution Type: ${contribution.contributionType || 'Not specified'}\n`;
        logContent += `  - Rate: ${contribution.contributionRate || 'Not specified'}%\n`;
        logContent += `  - Registry Identifier: ${contribution.registryIdentifier || 'Not specified'}\n`;
        
        if (contribution.error) {
          logContent += `  - ERROR: ${contribution.error}\n`;
        }
        
        // Status-based counting
        if (status === 'ACCEPTED' || status === 'ACCEPTED_WITH_ERROR_AKA') {
          totalAcceptedDetailed += contributionAmount;
          logContent += `  - ✅ COUNTED AS ACCEPTED\n`;
        } else if (status === 'PROCESSING') {
          totalProcessingDetailed += contributionAmount;
          logContent += `  - ⏳ IN PROCESSING\n`;
        } else if (status === 'ACTION_REQUIRED') {
          totalActionRequiredDetailed += contributionAmount;
          logContent += `  - ⚠️ REQUIRES ACTION\n`;
        }
        
        logContent += `\n`;
      });
    } else {
      logContent += `No fund contributions found\n`;
    }

    // Contributions summary
    logContent += `----------------------------\n`;
    logContent += `CONTRIBUTIONS SUMMARY:\n`;
    logContent += `Accepted contributions: ${totalAcceptedDetailed.toLocaleString()} ₪\n`;
    logContent += `Processing contributions: ${totalProcessingDetailed.toLocaleString()} ₪\n`;
    logContent += `Action required contributions: ${totalActionRequiredDetailed.toLocaleString()} ₪\n`;
    
    const totalAllContributions = totalAcceptedDetailed + totalProcessingDetailed + totalActionRequiredDetailed;
    logContent += `TOTAL SUM OF ALL CONTRIBUTIONS: ${totalAllContributions.toLocaleString()} ₪\n\n`;

    // Logic errors check
    logContent += `LOGIC ERRORS CHECK:\n`;
    logContent += `===================\n`;
    
    const acceptedPercentageDetailed = totalToBePaidDetailed > 0
      ? Math.round((totalAcceptedDetailed / totalToBePaidDetailed) * 100)
      : 0;
    
    logContent += `Percentage of accepted contributions: ${acceptedPercentageDetailed}%\n\n`;

    // Critical checks
    let hasErrors = false;
    
    if (totalAcceptedDetailed > totalToBePaidDetailed) {
      hasErrors = true;
      logContent += `🚨 CRITICAL ERROR!\n`;
      logContent += `Accepted contributions amount (${totalAcceptedDetailed.toLocaleString()} ₪) is greater than total file amount (${totalToBePaidDetailed.toLocaleString()} ₪)!\n`;
      logContent += `Difference: ${(totalAcceptedDetailed - totalToBePaidDetailed).toLocaleString()} ₪\n\n`;
    }

    if (totalAllContributions > totalToBePaidDetailed) {
      hasErrors = true;
      logContent += `🚨 CRITICAL ERROR!\n`;
      logContent += `Total sum of all contributions (${totalAllContributions.toLocaleString()} ₪) is greater than total file amount (${totalToBePaidDetailed.toLocaleString()} ₪)!\n`;
      logContent += `Difference: ${(totalAllContributions - totalToBePaidDetailed).toLocaleString()} ₪\n\n`;
    }

    if (!hasErrors) {
      logContent += `✅ No logic errors detected.\n`;
      logContent += `All calculations appear to be correct.\n\n`;
    }

    // Additional information
    logContent += `ADDITIONAL INFORMATION:\n`;
    logContent += `=======================\n`;
    logContent += `Number of deposits: ${Array.isArray(file?.deposits) ? file.deposits.length : 0}\n`;
    logContent += `Number of fund contributions: ${Array.isArray(file?.fundContributions) ? file.fundContributions.length : 0}\n`;
    logContent += `Number of employees: ${Array.isArray(file?.employees) ? file.employees.length : 0}\n`;
    
    logContent += `\n=======================\n`;
    logContent += `Report generated: ${new Date().toLocaleString('en-US')}\n`;
    logContent += `=======================\n`;

    // Create and download file
    const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `file-${file.id}-logs-${timestamp}.txt`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Also output to console for convenience
    console.log(`📁 File ${file.id} logs downloaded as: ${fileName}`);
  };

  // Simple calculations without logging
  const totalToBePaid = Array.isArray(file?.deposits)
    ? file.deposits.reduce(
      (accumulator: number, deposit: { totalDepositAmount?: number }) => {
        return accumulator + (deposit.totalDepositAmount || 0);
      },
      0,
    )
    : 0;

  const totalAcceptedContributions = Array.isArray(file?.fundContributions)
    ? file.fundContributions.reduce(
      (accumulator: number, contribution: FundContribution) => {
        if (contribution.status === 'ACCEPTED' || contribution.status === 'ACCEPTED_WITH_ERROR_AKA') {
          return accumulator + (contribution.contributionAmount || 0);
        }
        return accumulator;
      },
      0,
    )
    : 0;

  const totalProcessingContributions = Array.isArray(file?.fundContributions)
    ? file.fundContributions.reduce(
      (accumulator: number, contribution: FundContribution) => {
        if (contribution.status === 'PROCESSING') {
          return accumulator + (contribution.contributionAmount || 0);
        }
        return accumulator;
      },
      0,
    )
    : 0;

  const totalActionRequiredContributions = Array.isArray(file?.fundContributions)
    ? file.fundContributions.reduce(
      (accumulator: number, contribution: FundContribution) => {
        if (contribution.status === 'ACTION_REQUIRED') {
          return accumulator + (contribution.contributionAmount || 0);
        }
        return accumulator;
      },
      0,
    )
    : 0;

  const acceptedPercentage = totalToBePaid > 0
    ? Math.round((totalAcceptedContributions / totalToBePaid) * 100)
    : 0;

  return (
    <>

      <TableRow
        onClick={handleRowClick}
        sx={{
          backgroundColor: '#F2F4F7',
          borderRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          verticalAlign: 'bottom',
          cursor: 'pointer',
          position: 'relative',
        }}

      >
        <TableCell
          sx={{
            py: '20px',
            px: '30px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              -
            </Typography>
            {/* TEMPORARY TEST BUTTON FOR DEBUGGING */}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent file details from opening
                downloadLogsAsFile();
              }}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '10px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📁 DOWNLOAD LOGS
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent file details from opening
                logTotalCalculations();
              }}
              style={{
                marginTop: '4px',
                padding: '4px 8px',
                backgroundColor: '#4dabf7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '10px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🔍 CONSOLE
            </button>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            py: '20px',
            px: '10px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {file.salaryMonth || '-'}
            </Typography>

          </Box>
        </TableCell>

        <TableCell
          sx={{
            py: '20px',
            px: '10px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '2px',
            }}
          >
            <Stack>
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
              >
                {file.user.companyName || '-'}
              </Typography>
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
              >
                {file.user.companyRegistrationNumber || '-'}
              </Typography>
            </Stack>

          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {file?.paymentDate || '-'}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {formatMoney(totalToBePaid.toLocaleString())}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Typography
            fontWeight={'700'}
            fontSize={'14px'}
            fontFamily={'AlmoniDL'}
          >
            {formatMoney(totalAcceptedContributions.toLocaleString())}
          </Typography>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
            {file.status === 'COMPLETED' && (
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#94C729',
                }}
              />
            )}
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {formatMoney(totalProcessingContributions.toLocaleString())}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {formatMoney(totalActionRequiredContributions.toLocaleString())}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {acceptedPercentage}%
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            textAlign: 'center',
            verticalAlign: 'bottom',
            borderTopLeftRadius: '10px',
            borderBottomLeftRadius: '10px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            {file.status === 'COMPLETED' || file.status === 'PAID' ? (
              <Icon icon="mingcute:check-fill" style={{ color: '#94C729', fontSize: '20px' }} />
            ) : (
              <Icon icon="mingcute:close-fill" style={{ color: '#FF6F78', fontSize: '20px' }} />
            )}
          </Box>
        </TableCell>
      </TableRow>

      {openDetails && (
        <TableRow>
          <TableCell colSpan={11} sx={{ px: '50px', borderBottom: 'none', width: '100%' }}>
            <EmployeesList employees={file.employees} fundContributions={file.fundContributions} deposits={file.deposits} />
          </TableCell>
        </TableRow>
      )}

    </>
  );
};

export default FileItem;
